/**
 * Pseudonym constants shared across all TL4K apps (WordCloud, ScratchJr, etc.)
 *
 * Single source of truth for:
 *  - Animal list (emoji + label)
 *  - Sensory descriptors (sight / touch / taste) for inclusive pseudonyms
 *  - Shape list (gender-aware for Italian prepositions)
 *  - Letter set
 *  - formatPseudonym() display formatter
 *
 * Avatar uses hex from descriptors; pseudonym text uses label (e.g. LISCIO, DOLCE, ROSSO).
 */

// ── Types ──

export interface AnimalDef {
  id: string;
  emoji: string;
  label: string;
}

export type SensorySense = 'sight' | 'touch' | 'taste';

export interface SensoryDescriptor {
  id: string;
  label: string;
  sense: SensorySense;
  hex: string;
  light: string;
  gender: 'm' | 'f';
}

/** @deprecated Use SensoryDescriptor; kept for backward compatibility */
export interface ColorDef {
  id: string;
  label: string;
  hex: string;
  light: string;
  gender: 'm' | 'f';
}

export interface ShapeDef {
  id: string;
  label: string;
  gender: 'm' | 'f';
}

export interface PseudonymConfig {
  animal: string;
  animal_color: string;
  shape: string;
  shape_color: string;
  letter?: string;
}

// ── Data ──

export const ANIMALS: AnimalDef[] = [
  { id: 'gatto',    emoji: '🐱', label: 'GATTO' },
  { id: 'cane',     emoji: '🐶', label: 'CANE' },
  { id: 'orso',     emoji: '🐻', label: 'ORSO' },
  { id: 'volpe',    emoji: '🦊', label: 'VOLPE' },
  { id: 'leone',    emoji: '🦁', label: 'LEONE' },
  { id: 'panda',    emoji: '🐼', label: 'PANDA' },
  { id: 'gufo',     emoji: '🦉', label: 'GUFO' },
  { id: 'delfino',  emoji: '🐬', label: 'DELFINO' },
  { id: 'farfalla', emoji: '🦋', label: 'FARFALLA' },
  { id: 'aquila',   emoji: '🦅', label: 'AQUILA' },
  { id: 'coniglio', emoji: '🐰', label: 'CONIGLIO' },
  { id: 'polpo',    emoji: '🐙', label: 'POLPO' },
];

/**
 * All sensory descriptors: sight (colori), touch (tatto), taste (gusto).
 * Used for pseudonym text (label) and avatar rendering (hex).
 */
export const SENSORY_DESCRIPTORS: SensoryDescriptor[] = [
  // Vista (colori) — stessi id esistenti per retrocompatibilità
  { id: 'rosso',     label: 'ROSSO',     sense: 'sight', hex: '#E74C3C', light: '#FADBD8', gender: 'm' },
  { id: 'blu',       label: 'BLU',       sense: 'sight', hex: '#2E86C1', light: '#D4E6F1', gender: 'm' },
  { id: 'verde',     label: 'VERDE',     sense: 'sight', hex: '#28B463', light: '#D5F5E3', gender: 'm' },
  { id: 'giallo',    label: 'GIALLO',    sense: 'sight', hex: '#F1C40F', light: '#FEF9E7', gender: 'm' },
  { id: 'arancione', label: 'ARANCIONE', sense: 'sight', hex: '#E67E22', light: '#FDEBD0', gender: 'm' },
  { id: 'viola',     label: 'VIOLA',     sense: 'sight', hex: '#8E44AD', light: '#E8DAEF', gender: 'm' },
  { id: 'rosa',      label: 'ROSA',      sense: 'sight', hex: '#E91E8A', light: '#FDEDF4', gender: 'f' },
  { id: 'celeste',   label: 'CELESTE',   sense: 'sight', hex: '#3CC8DB', light: '#D1F2F7', gender: 'm' },
  // Tatto
  { id: 'liscio',    label: 'LISCIO',    sense: 'touch', hex: '#3CC8DB', light: '#D1F2F7', gender: 'm' },
  { id: 'ruvido',    label: 'RUVIDO',    sense: 'touch', hex: '#8E44AD', light: '#E8DAEF', gender: 'm' },
  { id: 'morbido',   label: 'MORBIDO',   sense: 'touch', hex: '#E91E8A', light: '#FDEDF4', gender: 'm' },
  { id: 'freddo',    label: 'FREDDO',    sense: 'touch', hex: '#2E86C1', light: '#D4E6F1', gender: 'm' },
  { id: 'caldo',     label: 'CALDO',     sense: 'touch', hex: '#E67E22', light: '#FDEBD0', gender: 'm' },
  { id: 'leggero',   label: 'LEGGERO',   sense: 'touch', hex: '#F1C40F', light: '#FEF9E7', gender: 'm' },
  { id: 'duro',      label: 'DURO',      sense: 'touch', hex: '#7F8C8D', light: '#EBEDEF', gender: 'm' },
  { id: 'vellutato', label: 'VELLUTATO', sense: 'touch', hex: '#28B463', light: '#D5F5E3', gender: 'm' },
  // Gusto
  { id: 'dolce',     label: 'DOLCE',     sense: 'taste', hex: '#E74C3C', light: '#FADBD8', gender: 'm' },
  { id: 'amaro',     label: 'AMARO',     sense: 'taste', hex: '#28B463', light: '#D5F5E3', gender: 'm' },
  { id: 'aspro',     label: 'ASPRO',     sense: 'taste', hex: '#F1C40F', light: '#FEF9E7', gender: 'm' },
  { id: 'sapido',    label: 'SAPIDO',    sense: 'taste', hex: '#8E44AD', light: '#E8DAEF', gender: 'm' },
  { id: 'fresco',    label: 'FRESCO',     sense: 'taste', hex: '#3CC8DB', light: '#D1F2F7', gender: 'm' },
  { id: 'piccante',  label: 'PICCANTE',  sense: 'taste', hex: '#E67E22', light: '#FDEBD0', gender: 'm' },
];

/** Solo descrittori visivi (colori); per retrocompatibilità con codice che usa COLORS. */
export const COLORS: ColorDef[] = SENSORY_DESCRIPTORS.filter(d => d.sense === 'sight').map(({ id, label, hex, light, gender }) => ({
  id,
  label,
  hex,
  light,
  gender,
}));

export const ANIMAL_COLORS = SENSORY_DESCRIPTORS;
export const SHAPE_COLORS = SENSORY_DESCRIPTORS;

export const SHAPES: ShapeDef[] = [
  { id: 'cerchio',   label: 'CERCHIO',   gender: 'm' },
  { id: 'quadrato',  label: 'QUADRATO',  gender: 'm' },
  { id: 'triangolo', label: 'TRIANGOLO', gender: 'm' },
  { id: 'stella',    label: 'STELLA',    gender: 'f' },
  { id: 'cuore',     label: 'CUORE',     gender: 'm' },
  { id: 'diamante',  label: 'DIAMANTE',  gender: 'm' },
];

export const LETTERS: string[] = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

// ── Formatting ──

/**
 * Format a pseudonym config into a human-readable Italian string.
 * Uses sensory descriptor labels (e.g. "GATTO SUL LISCIO NEL CERCHIO DOLCE A").
 */
export function formatPseudonym(config: PseudonymConfig): string {
  const a = ANIMALS.find(x => x.id === config.animal)?.label ?? config.animal.toUpperCase();
  const animalDesc = SENSORY_DESCRIPTORS.find(x => x.id === config.animal_color);
  const ac = animalDesc?.label ?? config.animal_color.toUpperCase();
  const prepColor = animalDesc?.gender === 'f' ? 'SULLA' : 'SUL';
  const shapeInfo = SHAPES.find(x => x.id === config.shape);
  const s = shapeInfo?.label ?? config.shape.toUpperCase();
  const shapeDesc = SENSORY_DESCRIPTORS.find(x => x.id === config.shape_color);
  const sc = shapeDesc?.label ?? config.shape_color.toUpperCase();
  const prepShape = shapeInfo?.gender === 'f' ? 'NELLA' : 'NEL';
  let out = `${a} ${prepColor} ${ac} ${prepShape} ${s} ${sc}`;
  if (config.letter) out += ` ${config.letter}`;
  return out;
}

/**
 * Get the emoji for a given animal ID. Returns '🐾' if not found.
 */
export function getAnimalEmoji(animalId: string): string {
  return ANIMALS.find(x => x.id === animalId)?.emoji ?? '🐾';
}

/**
 * Get the hex color for avatar rendering from a descriptor ID (sight, touch, or taste).
 * Returns '#888' if not found.
 */
export function getColorHex(descriptorId: string): string {
  return SENSORY_DESCRIPTORS.find(x => x.id === descriptorId)?.hex ?? '#888';
}
